import { Download } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { FormDatePicker, GridCardContent } from "components/atoms";
import { AppTable, ReportHeader } from "components/molecules";
import { FormikHelpers, useFormik } from "formik";
import { TableHelper, TimeHelper } from "helpers";
import { usePaginationRequest } from "hooks/useRequest";
import { IDownloadExcelRequest, IPaginateApiResponse, IReturn } from "model";
import { IGetForkliftPerformance } from "model/ForkLiftPerformance";
import moment, { Moment } from "moment";
import { useState } from "react";
import { DownloadActions } from "redux/actions";
import {
  useAppDispatch,
  useAppSelector,
} from "redux/configurations/reduxHooks";
import { ReportApiService } from "services";
import { CURRENT_PAGE_SIZE, EXPORT_FILE_NAME, FIRST_PAGE } from "utils/Const";
import { I18nTranslator, translations } from "utils/i18n";

const ExportSampleReport = () => {
  const dispatch = useAppDispatch();

  const [request, setRequest] = useState<any>({
    pageNumber: FIRST_PAGE,
    pageSize: CURRENT_PAGE_SIZE,
    fromDate: TimeHelper.startOfWeek("MM-DD-YYYY"),
    toDate: TimeHelper.endOfWeek("MM-DD-YYYY"),
  });

  const handleChangePage = (pageNumber: number) => {
    setRequest({
      ...request,
      pageNumber: pageNumber,
    });
  };

  const handlePageSizeChange = (pageSize: number) => {
    setRequest({ ...request, pageSize: pageSize });
  };

  const handleSearch = (searchText: string) => {
    setRequest({
      ...request,
      searchText,
    });
  };

  const { loading, result } = usePaginationRequest<any, any[]>(
    request,
    ReportApiService.getCycleCOuntQACollect
  );

  const downloadReturnReport = async () => {
    const params: IDownloadExcelRequest = {
      ...request,
      fileName: EXPORT_FILE_NAME.cycleCountQACollect,
      pageSize: (result as IPaginateApiResponse<any[]>).totalRecords,
      pageNumber: FIRST_PAGE,
    };
    dispatch(DownloadActions.downloadCycleCountQACollect.request(params));
  };

  return (
    <div
      style={{
        display: "flex",
        flex: 1,
        flexDirection: "column",
        height: "100%",
      }}
    >
      <DetailHeader
        request={request}
        onSubmit={setRequest}
        onDownload={downloadReturnReport}
      />
      <TableSection
        loading={loading}
        result={result}
        request={request}
        onSearch={handleSearch}
        onChangePage={handleChangePage}
        onPageSizeChange={handlePageSizeChange}
      />
    </div>
  );
};

const DetailHeader = (props: {
  request: IGetForkliftPerformance;
  onSubmit(values: any): void;
  onDownload(): void;
}) => {
  const { request, onSubmit, onDownload } = props;
  const downloading = useAppSelector(
    (state) => state.AppConfigReducer.downloading
  );

  const initialValues = {
    fromDate: moment(request.fromDate, "MM-DD-YYYY"),
    toDate: moment(request.toDate, "MM-DD-YYYY"),
  };
  const formik = useFormik<{
    fromDate: Moment;
    toDate: Moment;
  }>({
    initialValues,
    onSubmit: (values: any, { validateForm }: FormikHelpers<any>) => {
      const params = {
        pageNumber: 1,
        pageSize: request.pageSize,
        fromDate: TimeHelper.formatMomentToDate(values.fromDate, "MM-DD-YYYY"),
        toDate: TimeHelper.formatMomentToDate(values.toDate, "MM-DD-YYYY"),
      };
      onSubmit(params);
    },
  });

  return (
    <ReportHeader
      title={I18nTranslator(translations.routes.exportSample)}
      formik={formik}
      loading={false}
      renderButtons={
        <Stack direction={"row"} spacing={2}>
          <LoadingButton
            startIcon={<Download />}
            loading={downloading}
            variant="contained"
            onClick={onDownload}
          >
            {I18nTranslator(translations.button.exportDocs)}
          </LoadingButton>
        </Stack>
      }
    >
      <Grid container xs={12} item columnSpacing={2}>
        <Grid xs={3} item>
          <FormDatePicker
            id="fromDate"
            label={I18nTranslator(translations.fromDate)}
          />
        </Grid>
        <Grid xs={3} item>
          <FormDatePicker
            id="toDate"
            label={I18nTranslator(translations.toDate)}
          />
        </Grid>
        {/* <Grid xs={6} item>
          <FormTextField
            label={I18nTranslator(translations.report.type)}
            id="type"
            select
          >
            <MenuItem key="C" value={"C"}>
              Hàng QR
            </MenuItem>
            <MenuItem key="P" value={"P"}>
              Hàng không QR
            </MenuItem>
          </FormTextField>
        </Grid> */}
      </Grid>
    </ReportHeader>
  );
};

const TableSection = (props: {
  loading: boolean;
  result: IPaginateApiResponse<IReturn[]>;
  request: IGetForkliftPerformance;
  onChangePage(pageNumber: number): void;
  onPageSizeChange(pageSize: number): void;
  onSearch(searchText: string): void;
}) => {
  const { loading, result, onChangePage, onPageSizeChange, onSearch } = props;

  const COLUMNS: GridColDef[] = [
    TableHelper.renderSTT(),
    TableHelper.createCell({
      field: "createDate",
      headerName: I18nTranslator(translations.report.exportDate),
      size: "small",
      align: "left",
      renderCell: ({ value }) => {
        return (
          <Typography>{moment(value).format("DD-MM-YYYY HH:mm")}</Typography>
        );
      },
    }),
    TableHelper.createCell({
      field: "zoneName",
      headerName: I18nTranslator(translations.report.zoneName),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "lineName",
      headerName: I18nTranslator(translations.report.lineName),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "locationName",
      headerName: I18nTranslator(translations.report.locationName),
      align: "left",
    }),
    TableHelper.createCell({
      field: "palletCode",
      headerName: I18nTranslator(translations.report.palletCode),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "productLot",
      headerName: I18nTranslator(translations.report.productLot),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "productCode",
      headerName: I18nTranslator(translations.report.productCode),
      size: "small",
      align: "left",
    }),
    TableHelper.createCell({
      field: "productName",
      headerName: I18nTranslator(translations.report.productName),
      size: "large",
      align: "left",
    }),
    TableHelper.createCell({
      field: "productCodeOld",
      headerName: I18nTranslator(translations.report.productCodeOld),
      size: "small",
      align: "left",
    }),

    TableHelper.createCell({
      field: "quantity",
      headerName: I18nTranslator(translations.report.quantity),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell({
      field: "userName",
      headerName: "Người lấy",
      size: "small",
    }),
    TableHelper.createCell({
      field: "reason",
      headerName: I18nTranslator(translations.report.note),
      size: "full",
      align: "left",
    }),
  ];

  return (
    <AppTable<IReturn>
      loading={loading}
      data={result}
      columns={COLUMNS}
      onSearch={onSearch}
      searchPlaceholder={"Tìm kiếm"}
      onChangePage={onChangePage}
      hideHeader
      removeActionColumn
      defaultHeaderTable={170}
      onPageSizeChange={onPageSizeChange}
    />
  );
};

export default ExportSampleReport;
